import { Component } from 'preact';
import {
  GOLD, RADIUS, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED,
  BEVELED_BTN, PANEL_BG,
} from '../../lib/medievalTheme';
import { resolveLocalImageUrl } from '../../utils/localApi';
import { playSound } from '../../utils/game/sounds';
import { CloseButton } from '../ui/navButtons';
import { DESIGN_WIDTH, getDesignHeight, onCanvasScaleChange } from '../../utils/uiScale';
import { pushDismiss, removeDismiss } from '../../utils/escStack';
import { t } from '../../localization/t';

const CARD_ASPECT = 0.716;

const CARD_HEIGHT_VH = 44;

// Design-pixel constants on the 1600×900 design canvas. Multiplied by
// `canvasScale` at layout time so JS-driven positions and sizes track
// the cqw-driven CSS tokens (text-sm, p-N, etc.) at every canvas size.
// Without this scaling, on a >1.0× canvas the buttons stay 200 raw px
// while the text inside them grows with cqw, and "→ Cemetery" wraps;
// the close-button gap stays 40 raw px while the button itself grows,
// so it ends up sitting on the card.
const BUTTONS_WIDTH = 175;
const GROUP_GAP = 28;
const VIEWPORT_PAD = 24;
const CLOSE_BUTTON_GAP = 56;
const STRIP_GAP_ABOVE_GROUP = 150;
const GROUP_TOP_PAD = 20;

const EXIT_DURATION = 360;

const THUMB_W = 64;
const THUMB_H = THUMB_W / CARD_ASPECT;
const THUMB_GAP = 14;
const ACTIVE_SCALE = 1.12;

function getCanvasMetrics() {
  if (typeof document === 'undefined') {
    return { w: DESIGN_WIDTH, h: getDesignHeight(), scale: 1 };
  }
  const el = document.querySelector('.app-canvas');
  if (!el) return { w: window.innerWidth, h: window.innerHeight, scale: 1 };
  const r = el.getBoundingClientRect();
  return { w: r.width, h: r.height, scale: r.width / DESIGN_WIDTH };
}

const DECISION_LABELS = {
  hand: 'Hand',
  cemetery: 'Cem',
  // "opposite" label depends on scry direction, handled inline
};

/**
 * Scry dialog. Peek multiple cards into a thumbnail strip, then
 * click through them in any order and assign a destination for each.
 * Decisions accumulate in GameBoard (`scry.decisions`); cards without
 * a decision are kept in place. Done applies every decision
 * atomically; Cancel aborts without touching the pile.
 */
export default class ScryDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      canvas: getCanvasMetrics(),
      exiting: null,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    // Canvas dims also change on aspect-ratio switch without a resize event.
    this.unsubCanvasScale = onCanvasScaleChange(this.handleResize);
    if (this.props.scry && !this.state.exiting) {
      this._escDismissId = pushDismiss(() => this.props.onCancel?.());
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    this.unsubCanvasScale?.();
    removeDismiss(this._escDismissId);
    if (this.exitTimer) clearTimeout(this.exitTimer);
    if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
  }

  componentDidUpdate(prevProps, prevState) {
    const open = !!this.props.scry && !this.state.exiting;
    const wasOpen = !!prevProps.scry && !prevState.exiting;
    if (open && !wasOpen) this._escDismissId = pushDismiss(() => this.props.onCancel?.());
    else if (!open && wasOpen) removeDismiss(this._escDismissId);

    const prevCurrent = prevProps.scry?.peeked?.[prevProps.scry.activeIndex];
    const curCurrent = this.props.scry?.peeked?.[this.props.scry.activeIndex];
    if (prevCurrent && curCurrent && prevCurrent.id !== curCurrent.id && this.state.exiting) {
      this.setState({ exiting: null });
    }
    if (prevProps.scry && !this.props.scry && this.state.exiting) {
      this.setState({ exiting: null });
    }
  }

  handleResize = () => {
    if (this.resizeRaf) return;
    this.resizeRaf = requestAnimationFrame(() => {
      this.resizeRaf = null;
      this.setState({ canvas: getCanvasMetrics() });
    });
  };

  computeLayout() {
    const { scry } = this.props;
    const { canvas } = this.state;
    const s = canvas.scale;

    // Real-pixel sizes of the design constants on this canvas. Anchor
    // coords come from the WebGL canvas (canvas-local pixels), so all
    // layout is also in canvas-local pixels.
    const buttonsWidth = BUTTONS_WIDTH * s;
    const groupGap = GROUP_GAP * s;
    const pad = VIEWPORT_PAD * s;
    const topPad = GROUP_TOP_PAD * s;

    const cx = canvas.w / 2;
    const cy = canvas.h / 2;
    const anchor = scry.anchor || { x: cx, y: cy };

    const cardHeight = (canvas.h * CARD_HEIGHT_VH) / 100;
    const cardWidth = cardHeight * CARD_ASPECT;

    const dx = cx - anchor.x;
    const dy = cy - anchor.y;
    const len = Math.max(1, Math.sqrt(dx * dx + dy * dy));
    const drift = cardHeight * 0.55;
    const cardCenterX = anchor.x + (dx / len) * drift;
    const cardCenterY = anchor.y + (dy / len) * drift;

    const pileOnLeft = anchor.x <= cx;
    const groupWidth = cardWidth + groupGap + buttonsWidth;
    const groupHeight = cardHeight + 2 * topPad;

    let groupLeft;
    if (pileOnLeft) {
      groupLeft = cardCenterX - cardWidth / 2;
    } else {
      groupLeft = cardCenterX - cardWidth / 2 - groupGap - buttonsWidth;
    }
    let groupTop = cardCenterY - cardHeight / 2 - topPad;

    groupLeft = Math.max(pad, Math.min(groupLeft, canvas.w - groupWidth - pad));
    groupTop = Math.max(pad, Math.min(groupTop, canvas.h - groupHeight - pad));

    const cardRestX = pileOnLeft
      ? groupLeft + cardWidth / 2
      : groupLeft + buttonsWidth + groupGap + cardWidth / 2;
    const cardRestY = groupTop + topPad + cardHeight / 2;

    return {
      cardWidth,
      cardHeight,
      groupWidth,
      groupHeight,
      groupLeft,
      groupTop,
      buttonsFirst: !pileOnLeft,
      buttonsWidth,
      groupGap,
      cardRestX,
      cardRestY,
      anchor,
      canvasScale: s,
    };
  }

  getDestinationAnchor(destination) {
    const { scry } = this.props;
    const { canvas } = this.state;
    if (destination === 'hand') {
      return { x: canvas.w / 2, y: canvas.h + 80 * canvas.scale };
    }
    if (destination === 'cemetery') {
      return scry.cemeteryAnchor || scry.anchor || { x: canvas.w / 2, y: canvas.h };
    }
    // 'board'. The Summon exit (Cradle of Etherrum). The card flies
    // toward the table center where the spawn lands.
    if (destination === 'board') {
      return { x: canvas.w / 2, y: canvas.h / 2 };
    }
    // 'opposite' and 'same' both animate back to the pile.
    return scry.anchor || { x: canvas.w / 2, y: canvas.h };
  }

  beginExit(destination, commit) {
    if (this.state.exiting) return;
    const layout = this.computeLayout();
    const target = this.getDestinationAnchor(destination);
    const exitDx = target.x - layout.cardRestX;
    const exitDy = target.y - layout.cardRestY;
    this.setState({ exiting: { destination, exitDx, exitDy } });
    this.exitTimer = setTimeout(commit, EXIT_DURATION - 20);
  }

  handleAssignCurrent = (destination) => () => {
    // v1.64. Defensive guard against clicking a destination the
    // current scry effect disallows. ActionButton's disabled prop
    // already suppresses the click, but a programmatic invocation
    // (keyboard shortcut, future automation) would bypass that.
    const allowed = this.props.scry?.allowedDestinations;
    if (allowed && !allowed.has(destination)) return;
    playSound('scry');
    this.beginExit(destination, () => this.props.onAssignCurrent?.(destination));
  };

  handlePeekNext = () => {
    // No exit. Peek next just reveals another card and makes it
    // active. The new card plays its own reveal animation via the
    // key-swap on the large element.
    this.props.onPeekNext?.();
  };

  handleDone = () => {
    this.beginExit('keep', () => this.props.onKeepAndDone?.());
  };

  handleCast = () => {
    playSound('scry');
    this.beginExit('board', () => this.props.onCast?.());
  };

  handleCancel = () => {
    if (this.state.exiting) return;
    this.props.onCancel?.();
  };

  handleThumbClick = (idx) => {
    if (this.state.exiting) return;
    this.props.onSetActive?.(idx);
  };

  render() {
    const { scry } = this.props;
    if (!scry) return null;

    const { exiting } = this.state;
    const { pileName, from, peeked, decisions, activeIndex, remainingBelow, sameOrder = [], allowedDestinations, maxPeek, castOption } = scry;
    const fromTop = from === 'top';
    const oppositeLabel = fromTop ? t('Bottom') : t('Top');
    const sameLabel = fromTop ? t('Top') : t('Bottom');
    // v1.64. Peek next caps at maxPeek when card text restricts the
    // scry size (e.g. "Scry 2" can't reveal a 3rd card). Default
    // (maxPeek absent) keeps the existing pile-size cap.
    const peekedCount = peeked?.length || 0;
    const maxPeekReached = typeof maxPeek === 'number' && peekedCount >= maxPeek;
    const canPeekNext = remainingBelow > 0 && !exiting && !maxPeekReached;
    // v1.64. Per-effect destination gating. allowedDestinations is a
    // Set when the scry was opened by a card effect that restricts
    // where peeked cards can go; null/undefined keeps all four
    // buttons enabled.
    const allow = (dest) => !allowedDestinations || allowedDestinations.has(dest);

    const current = peeked?.[activeIndex];
    if (!current) return null;
    const currentDecision = decisions[current.id];
    // Position #1 always = next-to-draw, i.e. the most recently
    // clicked card. As the user stacks more cards, prior numbers
    // slide down, matching the physical "place on top" feel.
    const currentSamePosition = currentDecision === 'same'
      ? sameOrder.length - sameOrder.indexOf(current.id)
      : 0;

    const layout = this.computeLayout();
    const {
      cardWidth, cardHeight,
      groupWidth, groupHeight, groupLeft, groupTop,
      buttonsFirst,
      buttonsWidth, groupGap,
      cardRestX, cardRestY,
      anchor,
      canvasScale,
    } = layout;

    const startDx = anchor.x - cardRestX;
    const startDy = anchor.y - cardRestY;

    const exitVars = exiting ? {
      '--scry-exit-dx': exiting.exitDx + 'px',
      '--scry-exit-dy': exiting.exitDy + 'px',
      '--scry-exit-rot': (exiting.destination === 'hand' ? '-6deg' : '2deg'),
    } : {};

    const fadePad = 180 * canvasScale;
    const fadeLeft = groupLeft - fadePad;
    const fadeTop = groupTop - fadePad;
    const fadeWidth = groupWidth + fadePad * 2;
    const fadeHeight = groupHeight + fadePad * 2;

    // Card-effect scrys restrict where peeked cards can go; buttons
    // outside the allowed set are HIDDEN, not greyed. The dialog
    // shows only the choices the effect actually offers (a pure peek
    // shows just "leave it"; Cradle adds "Summon"). Free scrys
    // (allowedDestinations null) keep the full set.
    // Single-decision shapes don't need the multi-card flow: when
    // only 'same' is allowed there's nothing to reorder, so the
    // destination row collapses away and "leave it" is the Done
    // button. Peek next hides whenever the effect caps the reveal
    // at one card.
    const peekOnly = allowedDestinations
      && allowedDestinations.size === 1
      && allowedDestinations.has('same');
    const showPeekNext = !(typeof maxPeek === 'number' && maxPeek <= 1);
    const showDestinations = !peekOnly;
    const doneLabel = peekOnly ? (fromTop ? t('Leave on top') : t('Leave on bottom')) : t('Done');

    const buttonsColumn = (
      <div className="flex flex-col gap-2" style={{ width: buttonsWidth }}>
        {castOption && (
          <ActionButton
            onClick={this.handleCast}
            disabled={!!exiting}
            tone="primary"
            title={t('Summon this card here')}
            dataSound="none"
          >
            {t('Summon for ({n})', { n: castOption.need })}
          </ActionButton>
        )}
        {showDestinations && allow('hand') && (
          <ActionButton
            onClick={this.handleAssignCurrent('hand')}
            disabled={!!exiting}
            active={currentDecision === 'hand'}
            dataSound="none"
          >{t('→ Hand')}</ActionButton>
        )}
        {showDestinations && allow('same') && (
          <ActionButton
            onClick={this.handleAssignCurrent('same')}
            disabled={!!exiting}
            active={currentDecision === 'same'}
            title={fromTop ? t('Put back on top. Order is the click order') : t('Put back on bottom. Order is the click order')}
            dataSound="none"
          >
            {currentSamePosition > 0 ? t('→ {end} (#{n})', { end: sameLabel, n: currentSamePosition }) : t('→ {end}', { end: sameLabel })}
          </ActionButton>
        )}
        {showDestinations && allow('opposite') && (
          <ActionButton
            onClick={this.handleAssignCurrent('opposite')}
            disabled={!!exiting}
            active={currentDecision === 'opposite'}
            dataSound="none"
          >{t('→ {end}', { end: oppositeLabel })}</ActionButton>
        )}
        {showDestinations && allow('cemetery') && (
          <ActionButton
            onClick={this.handleAssignCurrent('cemetery')}
            tone="danger"
            disabled={!!exiting}
            active={currentDecision === 'cemetery'}
            dataSound="none"
          >{t('→ Cemetery')}</ActionButton>
        )}
        {(showDestinations || castOption) && (
          <div className="my-1 h-px" style={{ background: `${GOLD} 0.18)` }} />
        )}
        {showPeekNext && (
          <ActionButton
            onClick={this.handlePeekNext}
            disabled={!canPeekNext}
            tone="primary"
            title={canPeekNext ? t('Reveal another card from the pile') : t('No more cards in pile')}
          >
            {t('Peek next ▸')}
          </ActionButton>
        )}
        <ActionButton onClick={this.handleDone} disabled={!!exiting}>
          {doneLabel}
        </ActionButton>
      </div>
    );

    const cardColumn = (
      <div className="flex flex-col items-center" style={{ width: cardWidth }}>
        <div
          style={{
            width: cardWidth,
            height: cardHeight,
            perspective: '1200px',
            transformStyle: 'preserve-3d',
          }}
        >
          <div
            key={`card-${current.id}`}
            className={exiting ? 'scry-card-exit' : 'scry-card-reveal'}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: RADIUS.card,
              overflow: 'hidden',
              boxShadow: '0 30px 80px rgba(0,0,0,0.8), 0 0 50px rgba(180,140,60,0.08)',
              '--scry-dx': startDx + 'px',
              '--scry-dy': startDy + 'px',
              ...exitVars,
            }}
          >
            <img
              src={resolveLocalImageUrl(current.imageUrl)}
              alt={current.name}
              decoding="async"
              className="block w-full h-full object-cover select-none"
              draggable={false}
              style={{ transform: 'translateZ(0)', backfaceVisibility: 'hidden' }}
            />
          </div>
        </div>
        <div
          className="mt-2 text-sm font-semibold transition-opacity flex items-center gap-2"
          style={{
            color: TEXT_PRIMARY,
            textShadow: '0 2px 6px rgba(0,0,0,0.8)',
            opacity: exiting ? 0 : 1,
          }}
        >
          <span>{current.name}</span>
          {currentDecision && (
            <span
              className="px-2 py-0.5 rounded text-[0.625cqw] font-bold tracking-wider"
              style={decisionPillStyle(currentDecision)}
            >
              {decisionLabel(currentDecision, oppositeLabel, sameLabel, currentSamePosition)}
            </span>
          )}
        </div>
      </div>
    );

    // Strip geometry. Shown whenever 2+ cards are peeked. All sizes
    // are design-px multiplied by canvasScale so the strip stays
    // proportional to the rest of the dialog at any canvas size.
    const showStrip = peeked.length >= 2;
    const thumbW = THUMB_W * canvasScale;
    const thumbH = THUMB_H * canvasScale;
    const thumbGap = THUMB_GAP * canvasScale;
    const stripRowWidth = peeked.length * thumbW + (peeked.length - 1) * thumbGap;
    const stripContentWidth = stripRowWidth + (90 + 60) * canvasScale;
    const stripCenterX = groupLeft + groupWidth / 2;
    const stripLeft = stripCenterX - stripContentWidth / 2;
    const stripTop = Math.max(VIEWPORT_PAD * canvasScale, groupTop - STRIP_GAP_ABOVE_GROUP * canvasScale);

    return (
      <>
        <div
          className="fixed inset-0 z-[1198]"
          style={{ background: 'transparent' }}
          onClick={this.handleCancel}
        />

        <div
          className="fixed pointer-events-none z-[1199]"
          style={{
            left: fadeLeft,
            top: fadeTop,
            width: fadeWidth,
            height: fadeHeight,
            background: 'radial-gradient(ellipse closest-side at center, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.78) 45%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0.18) 88%, transparent 100%)',
            opacity: exiting ? 0.6 : 1,
            transition: 'opacity 240ms ease',
          }}
        />

        {showStrip && (
          <div
            className="fixed z-[1201] flex flex-col items-center"
            style={{
              left: stripLeft,
              top: stripTop,
              width: stripContentWidth,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="text-[0.625cqw] mb-2"
              style={{
                color: TEXT_MUTED,
                textShadow: '0 1px 4px rgba(0,0,0,0.9)',
                opacity: exiting ? 0 : 1,
                transition: 'opacity 180ms ease',
              }}
            >
              {t('Click a card to view · Decide one by one')}
            </div>
            <div className="flex items-center" style={{ gap: thumbGap }}>
              <div
                className="flex flex-col items-end mr-3 select-none"
                style={{ color: TEXT_PRIMARY, textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}
              >
                <div className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>
                  {t(pileName)}
                </div>
                <div className="flex items-center gap-1 text-xs font-bold arena-heading">
                  <span style={{ color: `${GOLD} 1)` }}>{sameLabel}</span>
                  <span style={{ color: TEXT_MUTED }}>▸</span>
                </div>
              </div>

              {peeked.map((card, idx) => {
                const isActive = idx === activeIndex;
                const decision = decisions[card.id];
                const samePosition = decision === 'same'
                  ? sameOrder.length - sameOrder.indexOf(card.id)
                  : 0;
                const scale = isActive ? ACTIVE_SCALE : 1;
                return (
                  <div
                    key={card.id}
                    className="relative"
                    style={{
                      transform: `scale(${scale})`,
                      transition: 'transform 160ms ease',
                    }}
                  >
                    {/* Position badge */}
                    <div
                      className="absolute -top-2 -left-2 flex items-center justify-center text-[0.625cqw] font-bold select-none z-10"
                      style={{
                        width: '1.125cqw',
                        height: '1.125cqw',
                        borderRadius: '50%',
                        background: isActive ? `${GOLD} 0.95)` : 'rgba(20,16,10,0.95)',
                        color: isActive ? '#2a1a05' : TEXT_PRIMARY,
                        border: `1px solid ${GOLD} ${isActive ? '1' : '0.55'})`,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.7)',
                        pointerEvents: 'none',
                      }}
                    >
                      {idx + 1}
                    </div>
                    <button
                      type="button"
                      onClick={() => this.handleThumbClick(idx)}
                      className="scry-thumb block rounded-card overflow-hidden cursor-pointer relative p-0"
                      style={{
                        width: thumbW,
                        height: thumbH,
                        outline: isActive ? `2px solid ${GOLD} 0.85)` : 'none',
                        outlineOffset: isActive ? '1px' : '0',
                        opacity: decision ? 0.78 : 1,
                        boxShadow: isActive
                          ? `0 0 16px rgba(180,140,60,0.55), 0 6px 16px rgba(0,0,0,0.7)`
                          : '0 6px 16px rgba(0,0,0,0.7)',
                        background: 'transparent',
                        border: 'none',
                      }}
                      title={card.name + (decision ? `. ${decisionLabel(decision, oppositeLabel, sameLabel, samePosition)}` : '')}
                    >
                      <img
                        src={resolveLocalImageUrl(card.imageUrl)}
                        alt={card.name}
                        decoding="async"
                        className="block w-full h-full object-cover select-none"
                        draggable={false}
                      />
                      {decision && (
                        <div
                          className="absolute left-0 right-0 bottom-0 text-center text-[0.5625cqw] font-bold py-0.5 tracking-widest"
                          style={decisionPillStyle(decision)}
                        >
                          {decisionLabel(decision, oppositeLabel, sameLabel, samePosition)}
                        </div>
                      )}
                    </button>
                  </div>
                );
              })}

              <div
                className="flex items-center ml-2 select-none text-xs"
                style={{ color: TEXT_MUTED, textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}
              >
                <span className="tracking-widest">{t('▸ deeper')}</span>
              </div>
            </div>
          </div>
        )}

        <div
          className="fixed z-[1202] flex items-center"
          style={{
            left: groupLeft,
            top: groupTop,
            width: groupWidth,
            gap: groupGap,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {buttonsFirst ? (
            <>
              {buttonsColumn}
              {cardColumn}
            </>
          ) : (
            <>
              {cardColumn}
              {buttonsColumn}
            </>
          )}
        </div>

        {/* Close button. Circular, pinned outside the group's top-
            right corner above the card. Both the size (2.5cqw) and
            the vertical/horizontal offsets scale with the canvas so
            the button never crowds or overlaps the card. */}
        <div
          className="fixed z-[1203]"
          style={{
            left: buttonsFirst
              ? groupLeft + groupWidth - 40 * canvasScale
              : groupLeft,
            top: groupTop - CLOSE_BUTTON_GAP * canvasScale,
            opacity: exiting ? 0 : 1,
            transition: 'opacity 180ms ease',
            pointerEvents: exiting ? 'none' : 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton
            title={t('Cancel scry')}
            onClick={this.handleCancel}
            style={{
              width: '2.5cqw',
              height: '2.5cqw',
              borderRadius: '50%',
              background: 'rgba(20,16,10,0.96)',
              border: `1px solid ${GOLD} 0.55)`,
              boxShadow: '0 4px 12px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,235,195,0.08)',
            }}
          />
        </div>
      </>
    );
  }
}

function decisionLabel(decision, oppositeLabel, sameLabel, samePosition) {
  if (decision === 'hand') return t('Hand');
  if (decision === 'cemetery') return t('Cemetery');
  if (decision === 'opposite') return oppositeLabel;
  if (decision === 'same') {
    const base = sameLabel || '';
    return samePosition > 0 ? t('{end} #{n}', { end: base, n: samePosition }) : base;
  }
  return '';
}

function decisionPillStyle(decision) {
  if (decision === 'hand') {
    return { background: 'rgba(180,140,60,0.85)', color: '#1a1208', textShadow: 'none' };
  }
  if (decision === 'cemetery') {
    return { background: 'rgba(180,60,60,0.85)', color: '#fff', textShadow: 'none' };
  }
  if (decision === 'same') {
    return { background: 'rgba(70,150,100,0.9)', color: '#fff', textShadow: 'none' };
  }
  return { background: 'rgba(80,120,180,0.85)', color: '#fff', textShadow: 'none' };
}

function ActionButton({ onClick, children, disabled = false, tone = 'default', title, active = false, dataSound }) {
  const toneStyle = tone === 'danger'
    ? { color: '#d48080', border: `1px solid rgba(196, 80, 80, ${active ? '0.85' : '0.45'})` }
    : tone === 'primary'
    ? { color: TEXT_PRIMARY, border: `1px solid ${GOLD} ${active ? '0.95' : '0.5'})` }
    : { color: active ? TEXT_PRIMARY : TEXT_BODY, border: active ? `1px solid ${GOLD} 0.6)` : undefined };

  return (
    <button
      type="button"
      disabled={disabled}
      title={title}
      data-sound={dataSound}
      className="scry-btn w-full px-4 py-2.5 text-sm font-medium cursor-pointer text-left disabled:opacity-40 disabled:cursor-not-allowed"
      style={{
        ...BEVELED_BTN,
        backgroundColor: PANEL_BG,
        borderRadius: 'var(--radius-lg)',
        ...toneStyle,
        boxShadow: active
          ? `inset 0 1px 0 rgba(255,255,255,0.08), 0 0 10px rgba(180,140,60,0.25), 0 2px 8px rgba(0,0,0,0.4)`
          : undefined,
      }}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
    >
      {children}
    </button>
  );
}
